import { Severity } from '@sentry/browser';
import { createAction } from '@reduxjs/toolkit';
import { createOrder } from '../../utils/serverAPI/paypal';
import { getAccountId } from '../../utils/serverAPI/getAccountId';
import { logGeneralError, logXhrError } from '../../utils/sentry';
import { type PaymentBreakdown } from '../../types/PaymentBreakdown';
import { type StoreState } from '../../types/StoreState';
import { selectAuthParams, selectSessionId } from '../../selectors';
import { type OrderItem } from '../../types/OrderItem';

export const createPayPalOrderAction = createAction<{
  orderId: string;
  paymentAmount: string;
  paymentBreakdown: PaymentBreakdown;
  orderItems: OrderItem[];
  chargeId?: string;
  cashierOrderId?: string;
}>('CREATE_PAYPAL_ORDER');

export interface CreatePayPalOrderParams {
  paymentAmount: string;
  currency: string;
  requestShipping: boolean;
  paymentBreakdown: PaymentBreakdown;
  orderItems: OrderItem[];
  appDefId: string;
  appInstanceId: string;
}
export const createPayPalOrder =
  ({
    paymentAmount,
    currency,
    requestShipping,
    paymentBreakdown,
    orderItems,
    appDefId,
    appInstanceId,
  }: CreatePayPalOrderParams) =>
  async (
    dispatch,
    getState: () => StoreState,
  ): Promise<{
    orderId: string;
    chargeId?: string;
    cashierOrderId?: string;
  }> => {
    const accountId = getAccountId(appDefId, appInstanceId);
    const sessionId = selectSessionId(getState());
    const reqArgs: Parameters<typeof createOrder>[0] = {
      accountId,
      paymentAmount,
      paymentBreakdown,
      orderItems,
      currencyCode: currency,
      requestShipping,
      sessionId,
    };
    try {
      const { orderId, chargeId, cashierOrderId } = await createOrder(reqArgs, selectAuthParams(getState()));

      dispatch(
        createPayPalOrderAction({
          orderId,
          chargeId,
          cashierOrderId,
          paymentAmount,
          paymentBreakdown,
          orderItems,
        }),
      );

      return { orderId, chargeId, cashierOrderId };
    } catch (e) {
      if (
        e?.response?.data?.message.match(/restricted|locked|inactive/) ||
        e?.response?.data?.message.match(/You do not have permissions/)
      ) {
        logGeneralError({
          message: 'Account is restricted|locked|inactive|not have permissions',
          payload: e.reponse.data,
          fingerprint: ['restrictedAccount'],
          severity: Severity.Info,
        });
      } else {
        logXhrError({
          axiosError: e,
          message: 'XHR: PayPal createOrder failed',
          fingerprint: ['createPayPalOrder'],
          reqArgs,
        });
      }
      throw e;
    }
  };
